import { CheckCircle } from '@mui/icons-material'
import { Box, Button, Theme } from '@mui/material'
import React, { useCallback } from 'react'
import { tokens } from 'src/styles/materialTheme'
import useDeviceType from 'src/rsmCoreComponents/hooks/useDeviceType'
import { EmptyBoxIcon } from '@rsmus/myrsm-icons'
import { useFeatureFlag } from '../../../utils/hooks/useFeatureFlag'
import { Styles } from '../../../types'
import { Interest } from '../types'

const styles: Styles = {
  check: (theme) => ({
    borderRadius: '50%',
    width: '1.5rem',
    height: '1.5rem',
    background:
      'radial-gradient(circle, rgba(255 255 255 / 1) 50%, rgba(255 255 255 / 0) 50%)',
    position: 'absolute',
    top: '-0.5rem',
    right: '-0.5rem',
    zIndex: 1,
    color: theme.palette.secondary.main,
    [theme.breakpoints.up('tablet')]: {
      top: '0.375rem',
      right: '0.375rem',
    },
    '& .MuiSvgIcon-root': {
      display: 'block',
    },
  }),
  checkRedesign: (theme) => ({
    borderRadius: '50%',
    width: '1.5rem',
    height: '1.5rem',
    background:
      'radial-gradient(circle, rgba(255 255 255 / 1) 50%, rgba(255 255 255 / 0) 50%)',
    position: 'absolute',
    top: '-0.5rem',
    right: '-0.5rem',
    zIndex: 1,
    color: theme.palette.secondary.main,
    marginRight: '0.375rem',
    [theme.breakpoints.up('tablet')]: {
      top: '0.375rem',
      right: '0.375rem',
    },
    '& .MuiSvgIcon-root': {
      display: 'block',
    },
  }),
  unchecked: (theme) => ({
    width: '1.25rem',
    height: '1.25rem',
    borderRadius: '50%',
    border: `0.125rem solid ${tokens.colors.white}`,
    backgroundColor: 'transparent',
    position: 'absolute',
    top: '-0.5rem',
    right: '-0.5rem',
    zIndex: 1,
    marginRight: '0.375rem',
    [theme.breakpoints.up('tablet')]: {
      top: '0.375rem',
      right: '0.375rem',
    },
  }),
  icon: (theme) => ({
    [theme.breakpoints.down('tablet')]: {
      display: 'none',
    },
  }),
  name: (theme) => ({
    fontSize: '0.75rem',
    lineHeight: '1rem',
    letterSpacing: '0.0625rem',
    textTransform: 'uppercase',
    [theme.breakpoints.up('tablet')]: {
      marginTop: '0.75rem',
    },
  }),
  redesignName: (theme) => ({
    color: tokens.colors.white,
    fontSize: '0.75rem',
    lineHeight: '1rem',
    letterSpacing: '0.0625rem',
    textTransform: 'uppercase',
    [theme.breakpoints.up('tablet')]: {
      marginTop: '0.75rem',
    },
    [theme.breakpoints.only('mobile')]: {
      textWrap: 'wrap',
    },
  }),
}

interface InterestTileProps {
  checked?: boolean
  index: number
  interest: Interest
  onClick: (check: boolean, interest: Interest) => void
  testId: string
}

const InterestTile = ({
  checked = false,
  index,
  interest,
  onClick,
  testId,
}: InterestTileProps) => {
  const { isMobile } = useDeviceType()
  const userRedesignFeatureEnabled = useFeatureFlag('UserProfile_Redesign')

  const getBackgroundColor = useCallback(() => {
    if (userRedesignFeatureEnabled) {
      return checked
        ? tokens.colors.midnightBlue
        : tokens.colors.rsmBlue.primary
    }
    return 'none'
  }, [userRedesignFeatureEnabled, checked])

  const computedStyles = {
    button: useCallback(
      (theme: Theme) => ({
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        borderColor: checked ? theme.palette.primary.main : tokens.colors.black,
        borderRadius: 0,
        borderStyle: userRedesignFeatureEnabled ? 'none' : 'solid',
        borderWidth: checked ? '0.125rem' : '0.0625rem',
        padding: userRedesignFeatureEnabled ? 0 : '0.5rem',
        width: '10rem',
        height: '4rem',
        textAlign: 'center',
        [theme.breakpoints.up('tablet')]: {
          height: userRedesignFeatureEnabled ? '6.25rem' : '10rem',
          width: userRedesignFeatureEnabled ? '8.125rem' : '10rem',
        },
        [theme.breakpoints.only('mobile')]: {
          backgroundColor: `${getBackgroundColor()} !important`, // important is needed so that base mui hover styling does not override the background color
          padding: '0.5rem',
        },
      }),
      [checked, userRedesignFeatureEnabled],
    ),
    container: useCallback(
      (theme: Theme) => ({
        marginY: '0.375rem',
        marginX: userRedesignFeatureEnabled ? 'unset' : '0.375rem',
        [theme.breakpoints.only('mobile')]: {
          margin: '0.375rem',
        },
      }),
      [userRedesignFeatureEnabled],
    ),
  }

  const getInterestIcon = () => {
    if (userRedesignFeatureEnabled) {
      let icon = <Box sx={styles.redesignName}>{interest.name}</Box>
      if (!isMobile && interest.icon) {
        icon = React.cloneElement(interest.icon, {
          color: checked ? undefined : tokens.colors.rsmBlue.primary,
        })
      }

      return interest.icon ? (
        icon
      ) : (
        <EmptyBoxIcon
          data-testid={`EmptyIcon_${testId}_${index}`}
          aria-label="Empty Icon"
        />
      )
    }
    return interest.icon ? (
      interest.icon
    ) : (
      <EmptyBoxIcon
        data-testid={`EmptyIcon_${testId}_${index}`}
        aria-label="Empty Icon"
      />
    )
  }

  return (
    <Box component="li" sx={(theme) => computedStyles.container(theme)}>
      <Button
        sx={(theme) => computedStyles.button(theme)}
        role="checkbox"
        aria-checked={checked}
        data-testid={`Btn_${testId}_${index}`}
        onClick={() => onClick(!checked, interest)}>
        {userRedesignFeatureEnabled && !checked && !isMobile && (
          <Box sx={styles.unchecked} />
        )}
        {checked && (
          <Box
            sx={
              userRedesignFeatureEnabled ? styles.checkRedesign : styles.check
            }>
            <CheckCircle />
          </Box>
        )}
        {userRedesignFeatureEnabled ? (
          <>
            {getInterestIcon()}
            <Box role="img" aria-label={interest.name} />
          </>
        ) : (
          <>
            <Box sx={styles.icon}>{getInterestIcon()}</Box>
            <Box sx={styles.name}>{interest.name}</Box>
          </>
        )}
      </Button>
    </Box>
  )
}

export default InterestTile
